import { useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { blogService } from "../services/blogService";
import { useEscapeKey } from "../hooks/useEscapeKey";
import { Input } from "./Input";
import { Button } from "./Button";

interface CreateBlogModalProps {
    isOpen: boolean;
    onClose: () => void;
}

export function CreateBlogModal({ isOpen, onClose }: CreateBlogModalProps) {
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [formData, setFormData] = useState({
        title: "",
        slug: ""
    });

    useEscapeKey(isOpen && !isLoading, onClose);

    const handleSubmitCreate = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        setError(null);

        try {
            const newBlog = await blogService.create(formData);
            onClose();
            // Reset form
            setFormData({ title: "", slug: "" });
            navigate(`/edit/${newBlog.id}`);
        } catch (err) {
            setError("Failed to create blog. Please try again.");
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    };

    if (!isOpen) return null;

    // Portalled to the body for the same reason as LoginModal: the navbar it
    // renders from is its own stacking context.
    return createPortal(
        <div className="modal modal-open" role="dialog">
            <div className="modal-box relative">
                <button
                    className="btn btn-sm btn-circle absolute right-2 top-2"
                    onClick={onClose}
                >
                    ✕
                </button>
                <h3 className="font-bold text-lg mb-4">Create New Blog</h3>

                <form onSubmit={handleSubmitCreate} className="flex flex-col gap-4">
                    <Input
                        label="Title"
                        placeholder="Enter blog title"
                        value={formData.title}
                        onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                        required
                    />

                    <Input
                        label="Slug"
                        placeholder="enter-blog-slug"
                        value={formData.slug}
                        onChange={(e) => setFormData(prev => ({ ...prev, slug: e.target.value }))}
                        required
                    />

                    {error && <div className="text-error text-sm">{error}</div>}

                    <div className="modal-action">
                        <Button
                            type="button"
                            onClick={onClose}
                            disabled={isLoading}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            className="btn-primary"
                            isLoading={isLoading}
                        >
                            Create
                        </Button>
                    </div>
                </form>
            </div>
            <button
                type="button"
                className="modal-backdrop"
                onClick={onClose}
                disabled={isLoading}
                aria-label="Close"
            />
        </div>,
        document.body
    );
}
